import React, { createRef } from "react";
import axios from "axios";
import { Link } from "react-router";
import moment from "moment";
import cx from "classnames";
import Select from "react-select";

import FormErrorHandler from "../../components/FormErrorHandler";
import Pagination from "../../components/Pagination";
import { courseStateNames } from "../courses/components/courseState";
import { scrollToElem, getCountryData } from "../../functions.js";

export default class LocationsForm extends React.Component {
    constructor(props) {
        super(props);

        this.cancelToken = axios.CancelToken;
        this.cancelSource = this.cancelToken.source();
        this.dropdownRef = createRef();
        this.timeoutId = null;
        this.postalCodeTimeout = null;

        this.state = {
            editMode: props.params.itemID !== undefined,
            dataTrainers: [],
            dataProvinces: [],
            errorMessage: {},
            readOnlyFields: [],
            company: "",
            name: "",
            street_name: "",
            number: "",
            postal_code: "",
            city: "",
            province: "",
            country: "NL",
            phone_number: "",
            is_active: 1,
            location_uuid: "",
            location_courses: [],
            count: 0,
            limit: 0,
            offset: 0,
            statusDropdownOpen: false,
            filters: {
                id: "",
                reference: "",
                display_name: "",
                state: []
            }
        };
    }

    updateElementState = (e) => {
        this.setState({
            [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value
        }, () => {
            if (["postal_code", "number"].includes(e.target.name)) {
                // if any of these fields are changed, we schedule a refresh of the address data:
                if (this.postalCodeTimeout) {
                    clearTimeout(this.postalCodeTimeout);
                }
                this.postalCodeTimeout = setTimeout(() => this.getAddressData(), 2000);
            }
        });
    };

    /**
     * Fetches the address data for the postal code and number in the state.
     * @returns {Promise<void>}
     */
    getAddressData = async () => {
        let {postal_code, number} = this.state;
        const regex = /^[1-9][0-9]{3}\s?[A-Za-z]{2}$/;
        if (!postal_code || !number || !regex.test(postal_code)) {
            // we don't have a valid number or postal code yet.
            return;
        }

        postal_code = postal_code.replace(/\W/g, "");
        number = number.replace(/[ˆ0-9]/g, ""); // only keep numbers

        try {
            const url = `/v1/postcode/${postal_code}/${number}/`;
            const response = await axios.get(url);
            const result = response.data;
            this.setState({
                street_name: result.street || "",
                city: result.city || "",
                province: result.state?.uuid || "",
                readOnlyFields: ["street_name", "city", "province"]
            });
        } catch (error) {
            console.error("Error fetching address data:", error);
            this.setState({
                readOnlyFields: []
            });
        }
    };

    handleSubmit = async (e) => {
        e.preventDefault();

        let postData = {
            name: this.state.name,
            company: this.state.company,
            street_name: this.state.street_name,
            number: this.state.number,
            postal_code: this.state.postal_code,
            city: this.state.city,
            country: this.state.country,
            phone_number: this.state.phone_number,
            is_active: this.state.is_active
        };

        if (this.state.dataProvinces.length) {
            postData.state = this.state.province;
        }

        if (this.state.editMode) {
            try {
                const response = await axios.patch("/v1/locations/" + this.props.params.itemID + "/", postData);

                this.props.addGlobalMessage({
                    title: `De locatie '${this.state.name}' is succesvol bijgewerkt`,
                    link: `locations/edit/${response.data.uuid}`
                });
                this.props.router.push("/locations");
            } catch (error) {
                this.setState({
                    errorMessage: error.response.data
                });
                scrollToElem(document.querySelector(".form-error"));
            }
        } else {
            try {
                const response = await axios.post("/v1/locations/", postData);

                this.props.addGlobalMessage({
                    title: `De locatie '${this.state.name}' is succesvol opgeslagen`,
                    link: `locations/edit/${response.data.uuid}`
                });
                this.props.router.push("/locations");
            } catch (error) {
                this.setState({
                    errorMessage: error?.response?.data
                });
                scrollToElem(document.querySelector(".form-error"));
            }
        }
    };

    async componentDidMount() {
        await this.getLocations();
        this.getTrainers();

        document.addEventListener("mousedown", this.handleClickOutside);
    }

    getTrainers = async () => {
        const { data } = await axios.get("/v1/trainers/?fields=uuid,name&pagination=false&ordering=name");

        this.setState({ dataTrainers: data });
    };

    getLocations = async () => {
        if (this.state.editMode) {
            const { data } = await axios.get("/v1/locations/" + this.props.params.itemID + "/");

            this.setState(
                {
                    location_uuid: data.uuid,
                    name: data.name,
                    company: data.company,
                    street_name: data.street_name,
                    number: data.number,
                    postal_code: data.postal_code,
                    city: data.city,
                    province: data.state,
                    country: data.country,
                    phone_number: data.phone_number,
                    is_active: data.is_active
                },
                () => this.getCourses(0)
            );

            this.getProvinces(data.country);
        } else {
            this.getProvinces(this.state.country);
        }
    };

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.dropdownRef?.current?.contains(event.target)) {
            return;
        }
        this.setState({ statusDropdownOpen: false });
    };

    getProvinces = async (country = this.state.country) => {
        const { data } = await axios.get(`/v1/states/?country=${country}`);

        this.setState({ dataProvinces: data.results });
    };

    cancelAxiosRequest = () => {
        this.cancelSource.cancel();

        this.setState({
            count: 0,
            limit: 0,
            offset: 0
        });
    };

    getCourses = async (offset) => {
        this.cancelAxiosRequest();
        this.cancelToken = axios.CancelToken;
        this.cancelSource = this.cancelToken.source();

        const response = await axios.get(`/v1/courses/?location=${this.state.location_uuid}&limit=20`, {
            params: {
                offset: offset,
                ordering: "-date",
                ...this.state.filters
            },
            cancelToken: this.cancelSource.token
        });

        this.setState({
            location_courses: response.data.results.sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime)),
            count: response.data.count,
            limit: response.data.limit,
            offset: response.data.offset
        });
    };

    handleFilterChange = (e) => {
        if (this.timeoutId) clearTimeout(this.timeoutId);

        this.setState({ filters: { ...this.state.filters, [e.target.name]: e.target.value } }, () => {
            this.timeoutId = setTimeout(() => this.getCourses(this.state.offset), 800);
        });
    };

    render() {
        return (
            <div>
                <div className="section-top">
                    <div className="breadcrumbs">
                        <Link to={"/locations"}>Locaties</Link>
                    </div>
                    <h1>Locatie {this.state.editMode ? "bewerken" : "toevoegen"}</h1>
                </div>

                <form onSubmit={this.handleSubmit}>
                    <div className="row">
                        <div className="col-6">
                            <div className="form-group">
                                <label>Opleider *</label>
                                <Select
                                    {...window.selectDefaults}
                                    name="company"
                                    placeholder="Kies een opleider"
                                    value={this.state.company}
                                    options={this.state.dataTrainers.map((trainer) => ({ label: trainer.name, value: trainer.uuid }))}
                                    onChange={(val) => this.setState({ company: val === null ? "" : val.value })}
                                    closeOnSelect={true}
                                />
                                <FormErrorHandler fieldName="company" errorObject={this.state.errorMessage} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <div className="form-group">
                                <label>Naam *</label>
                                <input
                                    type="name"
                                    name="name"
                                    ref="name"
                                    className="form-control form-control-md"
                                    required={true}
                                    onChange={this.updateElementState}
                                    value={this.state.name}
                                />
                                <FormErrorHandler fieldName="name" errorObject={this.state.errorMessage} />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-2">
                            <div className="form-group">
                                <label>Postcode</label>
                                <input
                                    type="postal_code"
                                    name="postal_code"
                                    ref="postal_code"
                                    className="form-control form-control-md"
                                    onChange={this.updateElementState}
                                    value={this.state.postal_code}
                                />
                                <FormErrorHandler fieldName="postal_code"
                                    errorObject={this.state.errorMessage}/>
                            </div>
                        </div>

                        <div className="col-2">
                            <div className="form-group">
                                <label>Huisnummer *</label>
                                <input
                                    type="text"
                                    name="number"
                                    ref="number"
                                    className="form-control form-control-md"
                                    required={true}
                                    onChange={this.updateElementState}
                                    value={this.state.number}
                                />
                                <FormErrorHandler fieldName="number"
                                    errorObject={this.state.errorMessage}/>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <div className="form-group">
                                <label>Straatnaam *</label>
                                <input
                                    type="street_name"
                                    name="street_name"
                                    ref="street_name"
                                    readOnly={this.state.readOnlyFields.includes("street_name")}
                                    className="form-control form-control-md"
                                    required={true}
                                    onChange={this.updateElementState}
                                    value={this.state.street_name}
                                />
                                <FormErrorHandler fieldName="street_name"
                                    errorObject={this.state.errorMessage}/>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <div className="form-group">
                                <label>Plaatsnaam *</label>
                                <input
                                    type="city"
                                    name="city"
                                    ref="city"
                                    readOnly={this.state.readOnlyFields.includes("city")}
                                    className="form-control form-control-md"
                                    required={true}
                                    onChange={this.updateElementState}
                                    value={this.state.city}
                                />
                                <FormErrorHandler fieldName="city"
                                    errorObject={this.state.errorMessage}/>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-3">
                            <div className="form-group">
                                <label>Land *</label>
                                <Select
                                    {...window.selectDefaults}
                                    name="country"
                                    required={true}
                                    placeholder="Kies een land"
                                    value={this.state.country}
                                    options={Object.entries(getCountryData()).map(([country]) => {
                                        return {label: country, value: country};
                                    })}
                                    onChange={(val) => {
                                        if (val === null) {
                                            this.setState({ country: "", dataProvinces: [] });
                                            return;
                                        }
                                        this.setState({ country: val.value }, () => {
                                            this.getProvinces(this.state.country);
                                        });
                                    }}
                                    closeOnSelect={true}
                                />
                                <FormErrorHandler fieldName="country" errorObject={this.state.errorMessage} />
                            </div>
                        </div>
                        {this.state.dataProvinces && this.state.dataProvinces.length > 0 ? (
                            <div className="col-3">
                                <div className="form-group">
                                    <label>Provincie *</label>
                                    <Select
                                        {...window.selectDefaults}
                                        name="province"
                                        placeholder="Kies een provincie"
                                        value={this.state.province}
                                        disabled={this.state.readOnlyFields.includes("province")}
                                        options={this.state.dataProvinces.map((province) => ({ label: province.name, value: province.uuid }))}
                                        onChange={(val) => this.setState({ province: val === null ? "" : val.value })}
                                        closeOnSelect={true}
                                    />
                                    <FormErrorHandler fieldName="state" errorObject={this.state.errorMessage} />
                                </div>
                            </div>
                        ) : (
                            false
                        )}
                    </div>
                    <div className="row">
                        <div className="col-3">
                            <div className="form-group">
                                <label>Telefoonnummer *</label>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    ref="phone_number"
                                    className="form-control form-control-md"
                                    required={true}
                                    onChange={this.updateElementState}
                                    value={this.state.phone_number}
                                />
                                <FormErrorHandler fieldName="phone_number" errorObject={this.state.errorMessage} />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <div className="form-group">
                                <div className="custom-control custom-checkbox">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        ref="is_active"
                                        className="custom-control-input"
                                        id="id_is_active"
                                        onChange={this.updateElementState}
                                        checked={this.state.is_active}
                                    />
                                    <label className="custom-control-label" htmlFor="id_is_active">
                                        Actief
                                    </label>
                                </div>
                                <div>Zet de locatie inactief om deze niet meer in de lijst van beschikbare locaties te tonen.</div>

                                <FormErrorHandler fieldName="phone_number" errorObject={this.state.errorMessage} />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <FormErrorHandler fieldName="non_field_errors" errorObject={this.state.errorMessage} />

                            <div className="form-group">
                                <button className="btn btn-primary" type="submit">
                                    Opslaan
                                </button>
                            </div>
                        </div>
                    </div>
                </form>

                {this.state.editMode && (
                    <>
                        <h3>Cursussen voor deze locatie</h3>

                        <table className="table table-hover table-clickable">
                            <thead>
                                <tr>
                                    <th width="100px">
                                        <div>ID</div>
                                        <input
                                            style={{ width: "65px" }}
                                            className="text-filter"
                                            name="id"
                                            value={this.state.filters.id}
                                            onChange={this.handleFilterChange}
                                        />
                                    </th>
                                    <th width="120">Datum</th>
                                    <th width="200px">
                                        <div>Referentie</div>
                                        <input
                                            style={{ width: "165px" }}
                                            className="text-filter"
                                            name="your_reference"
                                            value={this.state.filters.your_reference}
                                            onChange={this.handleFilterChange}
                                        />
                                    </th>
                                    <th width="200px">
                                        <div>Cursusnaam</div>
                                        <input
                                            style={{ width: "165px" }}
                                            className="text-filter"
                                            name="display_name"
                                            value={this.state.filters.display_name}
                                            onChange={this.handleFilterChange}
                                        />
                                    </th>
                                    <th width="150px">
                                        <div>Status</div>
                                        <div
                                            className="dropdown-filter-container"
                                            style={{ cursor: "pointer" }}
                                            onClick={(e) => {
                                                this.setState({ statusDropdownOpen: true });
                                            }}
                                        >
                                            <div>
                                                <div className="dropdown-filter-toggle__counter" style={{ padding: "12px 17px" }}>
                                                    {courseStateNames.length} <i className="fa fa-angle-down" />
                                                </div>
                                            </div>
                                            <div
                                                ref={this.dropdownRef}
                                                style={{ top: "22px", right: "68px" }}
                                                className={cx("dropdown-filter", { "dropdown-filter--open": this.state.statusDropdownOpen })}
                                            >
                                                {courseStateNames.map((item) => {
                                                    return (
                                                        <div key={item.slug} className="dropdown-filter__input">
                                                            <input
                                                                onChange={(e) => {
                                                                    this.setState(
                                                                        {
                                                                            filters: {
                                                                                ...this.state.filters,
                                                                                state: e.target.checked
                                                                                    ? [...this.state.filters.state, item.slug]
                                                                                    : this.state.filters.state.filter((i) => i !== item.slug)
                                                                            }
                                                                        },
                                                                        () => setTimeout(() => this.getCourses(this.state.offset), 600)
                                                                    );
                                                                }}
                                                                checked={this.state.filters.state.some((f) => f === item.slug) || false}
                                                                value={this.state.filters.state.find((i) => i === item.slug) || false}
                                                                type="checkbox"
                                                                className="dropdown-filter__checkbox"
                                                            />
                                                            <label htmlFor={item} className="dropdown-filter__label">
                                                                <span className={"course-status-indicator course-status-indicator--" + item.slug}>{item.name}</span>
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </th>
                                    <th width="100">
                                        <i className="fa fa-user" />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.location_courses.map((course) => {
                                    const eventCount = course.events?.length || course.events;
                                    const studentCount = course.students?.length || course.students;

                                    return (
                                        <tr
                                            key={course.uuid}
                                            onClick={() => {
                                                this.props.router.push("/courses/edit/" + course.uuid + "/students");
                                            }}
                                        >
                                            <td width="100px">{course.id}</td>
                                            <td width="120">
                                                {eventCount === 0 || !course.course_datetime?.start_datetime ? (
                                                    <>N.N.B.</>
                                                ) : (
                                                    <>{moment(course.course_datetime.start_datetime).format("DD-MM-YYYY")}</>
                                                )}
                                            </td>
                                            <td width="200px">{course.your_reference}</td>
                                            <td width="200px">{course.display_name}</td>
                                            <td width="150px">
                                                <span className={"course-status-indicator course-status-indicator--" + course?.state}>
                                                    {courseStateNames?.find((item) => item.slug === course?.state)?.name}
                                                </span>
                                            </td>

                                            <td width="100">
                                                {studentCount} / {course.expected_students}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <Pagination count={this.state.count} limit={this.state.limit} offset={this.state.offset} pageSwitch={(i) => this.getCourses(i)} />
                    </>
                )}
            </div>
        );
    }
}
